import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';

function RepairORReplacement() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalamt, setTotalamt] = useState(0);
    const [totalbills, setTotalbills] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        let totalamt = 0;
        let billNumbers = new Set();
        dataItem.forEach(item => {
            totalamt += item.Amount;
            billNumbers.add(item.BillNumber);
        });
        setTotalamt(totalamt);
        setTotalbills(billNumbers.size);
    }, [dataItem]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/RepairORReplacement/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') setDataItem(response.response_data);
            } catch (error) {
                console.error('Error fetching Repair/Replacement Details:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) => String(value).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Repair_Replacement Receipt');
        const headerCaption = `Repair / Replacement Receipt From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        worksheet.mergeCells('A1:K1');
        const captionCell = worksheet.getCell('A1');
        captionCell.value = headerCaption;
        captionCell.font = { bold: true, size: 14 };
        captionCell.alignment = { vertical: 'middle', horizontal: 'left' };
        const headerRowValues = ['Receipt Date', 'Time', 'Receipt Number', 'Customer Name', 'Phone Number', 'Sales Man', 'Item Description', 'Item Category', 'Amount', 'Remarks', 'Mode Of Payment'];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        worksheet.columns = [{ width: 15 }, { width: 10 }, { width: 18 }, { width: 20 }, { width: 15 }, { width: 18 }, { width: 25 }, { width: 18 }, { width: 12 }, { width: 20 }, { width: 18 }];
        filteredDataItem.forEach((rowData) => {
            const row = worksheet.addRow([formatDate(rowData.BillDate), rowData.BillTime, rowData.BillNumber, rowData.CustomerName, rowData.PhoneNumber, rowData.SalesManName, rowData.ItemDescription, rowData.ItemCategory, parseFloat(rowData.Amount.toFixed(2)), rowData.Remarks, rowData.ModeOfPayment]);
            row.eachCell((cell, colNumber) => {
                if (colNumber === 9) { cell.alignment = { vertical: 'middle', horizontal: 'right' }; cell.numFmt = '#,##0.00'; }
                else cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        const totalRow = worksheet.addRow(['Total Bills', totalbills, '', '', '', '', '', '', totalamt, '', '']);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            if (colNumber === 2 || colNumber === 9) { cell.alignment = { vertical: 'middle', horizontal: 'right' }; if (colNumber === 9) cell.numFmt = '#,##0.00'; }
            else cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'Repair_ReplacementReceipt.xlsx');
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Repair / Replacement Receipt</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchTableData} disabled={loading}>
                            <RefreshIcon /> Refresh
                        </button>
                    </div>
                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}><FeedIcon /> Excel</button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}><SearchIcon /> Search</button>
                    </div>
                    {showSearchInput && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            {loading && (
                <div className="pg-loading-overlay">
                    <div className="pg-loading-card">
                        <div className="pg-logo-loader">
                            <div className="pg-logo-block pg-block-green"></div>
                            <div className="pg-logo-block pg-block-blue"></div>
                            <div className="pg-logo-block pg-block-red"></div>
                            <div className="pg-logo-block pg-block-yellow"></div>
                        </div>
                        <p className="pg-loading-text">Loading...</p>
                    </div>
                </div>
            )}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Receipt Date</th>
                            <th>Receipt No</th>
                            <th>Customer Name</th>
                            <th>Phone Number</th>
                            <th>Sales Man</th>
                            <th>Item Description</th>
                            <th>Item Category</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th>Remarks</th>
                            <th>Mode Of Payment</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.length === 0
                            ? <tr><td colSpan={10} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filteredDataItem.map((data, index) => (
                                <tr key={index}>
                                    <td>{formatDate(data.BillDate)} {data.BillTime}</td>
                                    <td>{data.BillNumber}</td>
                                    <td>{data.CustomerName}</td>
                                    <td>{data.PhoneNumber}</td>
                                    <td>{data.SalesManName}</td>
                                    <td>{data.ItemDescription}</td>
                                    <td>{data.ItemCategory}</td>
                                    <td style={{ textAlign: 'right' }}>{data.Amount.toFixed(2)}</td>
                                    <td>{data.Remarks}</td>
                                    <td>{data.ModeOfPayment}</td>
                                </tr>
                            ))
                        }
                        <tr style={{ background: 'linear-gradient(135deg, #1c2a3a 0%, #243447 100%)', color: '#e8f0f8', fontWeight: 700 }}>
                            <td style={{ padding: '6px 10px' }}>Total Bills</td>
                            <td style={{ padding: '6px 10px' }}>{totalbills}</td>
                            <td colSpan={5}></td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalamt.toFixed(2)}</td>
                            <td colSpan={2}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default RepairORReplacement;
